import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import type { TeamWithActions, Team, Project } from '@/shared/types'

async function fetchTeams(): Promise<TeamWithActions[]> {
  // Fetch teams the user has access to
  const { data: teams, error: teamsError } = await supabase
    .from('teams')
    .select('*')
    .order('updated_at', { ascending: false })

  if (teamsError) {
    throw new Error(teamsError.message)
  }

  if (!teams || teams.length === 0) {
    return []
  }

  // Fetch active projects for each team
  const teamIds = teams.map((t) => t.id)
  const { data: projects, error: projectsError } = await supabase
    .from('projects')
    .select('id, short_id, team_id, name, description, status, is_active, test_environment_url, objective, target_users, existing_assets, constraints, inspirations, specs_pdf_url, rejection_reason, current_version, created_at, updated_at, completed_at')
    .in('team_id', teamIds)
    .eq('is_active', true)

  if (projectsError) {
    throw new Error(projectsError.message)
  }

  // Get project IDs for fetching features
  const projectIds = projects?.map((p) => p.id) || []

  // Fetch features with user stories for active projects
  const actionCountsByTeam = new Map<string, number>()

  if (projectIds.length > 0) {
    const { data: features, error: featuresError } = await supabase
      .from('features')
      .select(`
        id,
        project_id,
        user_stories (id, status)
      `)
      .in('project_id', projectIds)

    if (featuresError) {
      throw new Error(featuresError.message)
    }

    // Calculate action counts per team
    const projectToTeam = new Map<string, string>()
    projects?.forEach((p) => {
      projectToTeam.set(p.id, p.team_id)
    })

    features?.forEach((feature) => {
      const teamId = projectToTeam.get(feature.project_id)
      if (!teamId) return

      const userStories = (feature.user_stories as { id: string; status: string }[]) || []
      const actionCount = userStories.filter(
        (us) => us.status === 'pending_validation' || us.status === 'testing'
      ).length

      const currentCount = actionCountsByTeam.get(teamId) || 0
      actionCountsByTeam.set(teamId, currentCount + actionCount)
    })
  }

  // Map projects to teams
  const projectsByTeamId = new Map<string, Project>()
  projects?.forEach((project) => {
    projectsByTeamId.set(project.team_id, project as Project)
  })

  // Combine teams with their active projects and action counts
  const teamsWithActions: TeamWithActions[] = teams.map((team) => ({
    ...(team as Team),
    active_project: projectsByTeamId.get(team.id),
    actionCount: actionCountsByTeam.get(team.id) || 0,
  }))

  return teamsWithActions
}

export function useTeams() {
  return useQuery({
    queryKey: ['teams'],
    queryFn: fetchTeams,
  })
}
